import ClassPerformance from 'components/GradingInsights/ClassPerformance';
import GradingProgress from 'components/GradingInsights/GradingProgress';
import GradingStatistics from 'components/GradingInsights/GradingStatistics';
import LmsSyncStatus from 'components/InstructorSubmissions/LmsSyncStatus';
import SemanticGradeDisplay from 'components/InstructorSubmissions/SemanticGradeDisplay';
import SubmissionSelectionCount from 'components/InstructorSubmissions/SubmissionSelectionCount';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
    Avatar,
    Button,
    Checkbox,
    Container,
    Icon,
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableRow,
    ScrollableTableSortableHeaderCells,
    Stack,
    styled,
    Tabs,
    Tag,
    Text,
    TextInput,
} from 'shell/components';
import { Placeholder, SubmissionTabs } from 'utils/constants';
import { getLatestPushbackRecord } from 'utils/gradePushback';
import { getLatestConfirmedId, getSubmissionGradingTime, getUserPreferredFullName } from 'utils/helper';

const Tablet = ({
    assignment,
    sortedFilteredSubmissions,
    duplicateSubmissions,
    activeTab,
    setActiveTab,
    checked,
    setChecked,
    filters,
    setFilters,
    getFilterCount,
    sortState,
    handleSetSortState,
    getSubmissionSection,
    getSubmissionStatus,
    getSubmissionFinalGrade,
    onClickBack,
    onClickViewAll,
    onClickViewSubmission,
    resetSubmissions,
    exportSubmissions,
    handleAcceptAll,
    canAcceptAllSuggestions,
    pushGrades,
    gradingInsightsData,
    columns,
}) => {
    const { accountUsers } = useApp();
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');

    const {
        gradingProgress,
        predictedGradingTime,
        setPredictedGradingTime,
        statisticsFilters,
        setStatisticsFilters,
        exportStatistics,
        calculatedTimeSavedPerSub,
        totalTimeSaved,
        handleCalculateTimeSave,
        handleResetTimeSave,
        sortedGraders,
        graderSortState,
        handleSetGraderSortState,
        graderColumns,
        classPerformanceStats,
        classPerformanceChartData,
        performanceFilters,
        setPerformanceFilters,
        performanceFilterOptions,
        handlePerformanceGraderFilter,
        studentGradingStatistics,
        visibleColumns,
        onColumnToggle,
        isTableExpanded,
        onToggleTableExpanded,
        studentSortState,
        handleSetStudentSortState,
        handleStatisticsGraderFilter,
        gradingStatisticsColumns,
    } = gradingInsightsData;

    const submissionRows = useMemo(() => {
        const usersMap = new Map(accountUsers.map((u) => [u.owner_account_id, u]));
        return sortedFilteredSubmissions.map((submission, index) => {
            const graderOwnerAccountId = getLatestConfirmedId(submission.sessions);
            const graderAccountUser = usersMap.get(graderOwnerAccountId);
            return (
                <CustomScrollableTableRow key={submission.id || index} isDuplicate={submission?.isDuplicate}>
                    <ScrollableTableCell isFixed>
                        <Checkbox
                            checked={checked.includes(submission.id)}
                            onChange={() => {
                                setChecked((current) => {
                                    if (current.includes(submission.id)) {
                                        return current.filter((id) => id !== submission.id);
                                    }
                                    return [...current, submission.id];
                                });
                            }}
                        />
                    </ScrollableTableCell>
                    {assignment?.api_assignment_id && (
                        <ScrollableTableCell isFixed>
                            <Stack sx={{ flexWrap: 'wrap' }} direction={'row'} spacing={2}>
                                {submission?.api?.user?.name ?? submission?.api?.user?.id}
                                {submission?.isDuplicate && <Tag type={'general'} value={'duplicate'} />}
                            </Stack>
                        </ScrollableTableCell>
                    )}
                    <ScrollableTableCell
                        sx={{ wordWrap: 'break-word', whiteSpace: 'normal !important' }}
                        isFixed={!Boolean(assignment?.api_assignment_id)}
                    >
                        {submission.files
                            .map(({ name, original_file }) => (original_file ? original_file.name : name))
                            .join(', ')}
                    </ScrollableTableCell>
                    {assignment?.api_assignment_id && (
                        <ScrollableTableCell>{getSubmissionSection(submission)}</ScrollableTableCell>
                    )}
                    <ScrollableTableCell>{getSubmissionStatus(submission)}</ScrollableTableCell>
                    <ScrollableTableCell>{getSubmissionFinalGrade(submission)}</ScrollableTableCell>
                    {assignment?.api_assignment_id && (
                        <>
                            <ScrollableTableCell>
                                <LmsSyncStatus assignment={assignment} submission={submission} />
                            </ScrollableTableCell>
                            <ScrollableTableCell>
                                <SemanticGradeDisplay
                                    fallback={Placeholder.NO_DATA}
                                    snapshot={getLatestPushbackRecord(submission)}
                                />
                            </ScrollableTableCell>
                        </>
                    )}
                    <ScrollableTableCell>{getSubmissionGradingTime(submission)}</ScrollableTableCell>
                    <ScrollableTableCell>
                        {graderAccountUser ? (
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Avatar user={graderAccountUser} />
                                {getUserPreferredFullName(graderAccountUser)}
                            </Stack>
                        ) : (
                            Placeholder.NO_DATA
                        )}
                    </ScrollableTableCell>
                    <ScrollableTableCell isFixed>
                        <Button
                            disabled={!submission.grading}
                            variant={'borderless'}
                            onClick={() => onClickViewSubmission(submission)}
                        >
                            {t('common:controls.view', 'View')}
                        </Button>
                    </ScrollableTableCell>
                </CustomScrollableTableRow>
            );
        });
    }, [
        accountUsers,
        assignment,
        checked,
        getSubmissionFinalGrade,
        getSubmissionSection,
        getSubmissionStatus,
        onClickViewSubmission,
        setChecked,
        sortedFilteredSubmissions,
        t,
    ]);

    return (
        <Container sx={{ height: '100%' }} device={'tablet'}>
            <RootStack spacing={5}>
                <Button
                    sx={{ alignSelf: 'flex-start' }}
                    startIcon={<Icon kind={'arrowLeft'} />}
                    variant={'borderless'}
                    onClick={onClickBack}
                >
                    {t('common:controls.back', 'Back')}
                </Button>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                        {assignment.title}
                    </Text>
                </Stack>

                {auth.role === 'instructor' && (
                    <Tabs
                        aria-label={'course lobby tabs'}
                        options={[
                            {
                                key: SubmissionTabs.SUBMISSIONS,
                                value: t('submission.submissions-label', 'Submissions'),
                            },
                            {
                                key: SubmissionTabs.GRADING_INSIGHTS,
                                value: t('submission.grading-insights-label', 'Grading Insights'),
                            },
                        ]}
                        selected={activeTab}
                        onChange={setActiveTab}
                    />
                )}
                {activeTab === SubmissionTabs.SUBMISSIONS && (
                    <>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                <Button
                                    sx={{ flexShrink: 0 }}
                                    color={'neutral'}
                                    startIcon={<Icon kind={'filter'} />}
                                    variant={'secondary'}
                                    onClick={() => setFilters((current) => ({ ...current, open: true }))}
                                >
                                    {t('common:controls.sort-filter', 'Sort & Filter')} (
                                    {getFilterCount(filters, sortState)})
                                </Button>
                                <TextInput
                                    sx={{ width: '150px' }}
                                    endIcon={<Icon kind={'search'} />}
                                    placeholder={t('common:controls.search', 'Search')}
                                    value={filters.search}
                                    onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
                                />
                            </Stack>
                            <Stack alignItems={'center'} direction={'row'} spacing={3}>
                                <Stack direction='row'>
                                    {assignment?.api_assignment_id && auth.role === 'instructor' && (
                                        <>
                                            {checked.length > 0 ? (
                                                <>
                                                    <Button
                                                        color={'neutral'}
                                                        variant={'tertiary'}
                                                        onClick={() => setChecked([])}
                                                    >
                                                        {t('common:controls.cancel', 'Cancel')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    disabled={assignment.api_assignment_id && auth.role === 'ta'}
                                                    startIcon={<Icon kind={'refresh'} />}
                                                    variant={'tertiary'}
                                                    color={'destructive'}
                                                    onClick={resetSubmissions}
                                                    tooltip={{
                                                        component: t(
                                                            'submission.reset-grades-feedback-tooltip',
                                                            'Reset grades and feedback for all submissions'
                                                        ),
                                                    }}
                                                >
                                                    {t('common:controls.reset', 'Reset')}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    <Button
                                        startIcon={<Icon kind={'download'} />}
                                        variant={'tertiary'}
                                        tooltip={{
                                            component: t(
                                                'submission.export-grades-feedback-tooltip',
                                                'Export grades and/or feedback to a CSV'
                                            ),
                                        }}
                                        onClick={exportSubmissions}
                                    >
                                        {t('common:controls.export', 'Export')}
                                    </Button>
                                </Stack>
                                {assignment?.ai_grading_enabled && assignment?.features?.accept_all && (
                                    <Button
                                        disabled={!canAcceptAllSuggestions}
                                        variant={'secondary'}
                                        startIcon={<Icon kind={'checkmark'} />}
                                        tooltip={{
                                            component: t(
                                                'submission.accept-all-suggestions-tooltip',
                                                'Accept all grading suggestions by AI'
                                            ),
                                        }}
                                        onClick={handleAcceptAll}
                                    >
                                        {t('common:ui.accept-all', 'Accept All')}
                                    </Button>
                                )}
                                {process.env.REACT_APP_AUTH === 'API' && assignment?.api_assignment_id && (
                                    <Button
                                        disabled={
                                            !sortedFilteredSubmissions.some(({ grading }) => grading) ||
                                            (assignment.api_assignment_id && auth.role === 'ta')
                                        }
                                        startIcon={<Icon kind={'arrowRight'} />}
                                        tooltip={{
                                            component:
                                                auth.role === 'ta'
                                                    ? 'Only instructors can pass back grades/feedback to your LMS.'
                                                    : 'Push grades and/or feedback back to your LMS',
                                        }}
                                        onClick={pushGrades}
                                    >
                                        {t('common:controls.push-to-lms', 'Push to LMS')}
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                        <SubmissionSelectionCount count={checked.length} />
                        {duplicateSubmissions.length > 0 && (
                            <Text color={'error'}>
                                <Trans
                                    i18nKey={'assignment:submission.duplicate-warning'}
                                    values={{ count: duplicateSubmissions.length }}
                                    default={
                                        "We've identified <bold>{{count}} duplicate submission(s)</bold>. Please remove them if necessary."
                                    }
                                    components={{
                                        bold: <Text color={'inherit'} component={'span'} weight={'semibold'} />,
                                    }}
                                />
                            </Text>
                        )}

                        <CustomScrollableTable
                            aria-label={'submissions'}
                            isLMS={Boolean(assignment?.api_assignment_id)}
                            s
                        >
                            <ScrollableTableHead>
                                <ScrollableTableRow>
                                    <ScrollableTableCell isFixed>
                                        <Checkbox
                                            checked={
                                                sortedFilteredSubmissions.length > 0 &&
                                                sortedFilteredSubmissions.every((s) => checked.includes(s.id))
                                            }
                                            onChange={() => {
                                                setChecked(() => {
                                                    return sortedFilteredSubmissions.every((s) =>
                                                        checked.includes(s.id)
                                                    )
                                                        ? []
                                                        : sortedFilteredSubmissions.map(({ id }) => id);
                                                });
                                            }}
                                        />
                                    </ScrollableTableCell>
                                    <ScrollableTableSortableHeaderCells
                                        columns={columns}
                                        sortState={sortState}
                                        handleSetSortState={handleSetSortState}
                                    />

                                    <ScrollableTableCell isFixed>
                                        <Stack
                                            alignItems={'center'}
                                            direction={'row'}
                                            justifyContent={'space-between'}
                                            spacing={2}
                                        >
                                            <Text>{t('grades-feedback', 'Grades/Feedback')}</Text>
                                            <Button
                                                disabled={sortedFilteredSubmissions.every(
                                                    (submission) => !submission.grading
                                                )}
                                                variant={'borderless'}
                                                onClick={onClickViewAll}
                                            >
                                                {t('common:controls.view-all', 'View All')}
                                            </Button>
                                        </Stack>
                                    </ScrollableTableCell>
                                </ScrollableTableRow>
                            </ScrollableTableHead>
                            <ScrollableTableBody>{submissionRows}</ScrollableTableBody>
                        </CustomScrollableTable>
                    </>
                )}
                {auth.role === 'instructor' && activeTab === SubmissionTabs.GRADING_INSIGHTS && (
                    <Stack spacing={5} sx={{ pb: '3rem' }}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'} spacing={4}>
                            <SubHeading variant={'h2'} weight={'medium'}>
                                {t('submission.grading-insights-label', 'Grading Insights')}
                            </SubHeading>
                            <Button
                                startIcon={<Icon kind={'download'} />}
                                variant={'secondary'}
                                onClick={gradingInsightsData.exportGraderStatistics}
                            >
                                {t('common:controls.export-to-csv', 'Export to CSV')}
                            </Button>
                        </Stack>
                        <GradingProgress
                            gradingProgress={gradingProgress}
                            predictedGradingTime={predictedGradingTime}
                            setPredictedGradingTime={setPredictedGradingTime}
                            calculatedTimeSavedPerSub={calculatedTimeSavedPerSub}
                            totalTimeSaved={totalTimeSaved}
                            handleCalculateTimeSave={handleCalculateTimeSave}
                            handleResetTimeSave={handleResetTimeSave}
                            sortedGraders={sortedGraders}
                            graderSortState={graderSortState}
                            handleSetGraderSortState={handleSetGraderSortState}
                            columns={graderColumns}
                        />
                        <ClassPerformance
                            assignment={assignment}
                            submissions={sortedFilteredSubmissions}
                            classPerformanceStats={classPerformanceStats}
                            classPerformanceChartData={classPerformanceChartData}
                            performanceFilters={performanceFilters}
                            setPerformanceFilters={setPerformanceFilters}
                            filterOptions={performanceFilterOptions}
                            onGraderFilterChange={handlePerformanceGraderFilter}
                        />
                        <GradingStatistics
                            assignment={assignment}
                            submissions={sortedFilteredSubmissions}
                            statisticsFilters={statisticsFilters}
                            setStatisticsFilters={setStatisticsFilters}
                            onGraderFilterChange={handleStatisticsGraderFilter}
                            exportStatistics={exportStatistics}
                            filterOptions={performanceFilterOptions}
                            tableData={studentGradingStatistics}
                            visibleColumns={visibleColumns}
                            onColumnToggle={onColumnToggle}
                            isTableExpanded={isTableExpanded}
                            onToggleTableExpanded={onToggleTableExpanded}
                            sortState={studentSortState}
                            handleSortState={handleSetStudentSortState}
                            columns={gradingStatisticsColumns}
                            isLmsAssignment={Boolean(assignment?.api_assignment_id)}
                        />
                    </Stack>
                )}
            </RootStack>
        </Container>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
        minHeight: '100%',
    };
});

const SubHeading = styled(Text)(() => {
    return {
        fontSize: '1.125rem',
        lineHeight: '1.5rem',
    };
});

const CustomScrollableTable = styled(ScrollableTable)(({ theme, isLMS }) => {
    return {
        flex: '1',
        '& .MuiTableCell-root': {
            '&:nth-child(1)': {
                left: 0,
                minWidth: '74px',
                maxWidth: '74px',
                width: '74px',
            },
            '&:nth-child(2)': {
                left: '74px',
                minWidth: '150px',
                maxWidth: '150px',
                width: '150px',
            },
            ...(isLMS && {
                '&:nth-child(3)': {
                    left: '224px',
                    minWidth: '250px',
                    maxWidth: '250px',
                    width: '250px',
                },
            }),
            '&:last-child': {
                right: 0,
                borderLeft: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                minWidth: '225px',
                maxWidth: '225px',
                width: '225px',
            },
            '&:nth-last-child(2)': {
                borderRight: 'none',
            },
        },
    };
});

const CustomScrollableTableRow = styled(ScrollableTableRow)(({ theme, isDuplicate }) => {
    return isDuplicate
        ? {
              '& > td.MuiTableCell-root': {
                  background: `${theme.palette.ensightful.error[50]} !important`,
              },
          }
        : {};
});

export default Tablet;
